import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import userContext from "../../../context/user-context";
import Get from "../../Utils/API/Get";
import DateFormated from "../../Utils/Date/Date";
import Export from "../../Utils/XLS/Export";
import InvoiceTable from "../Table/InvoiceTable";
import axios from "axios";
import Sorter from "../../UI/Toolbar/Sorter";
import Currency from "../../Utils/Number/Currency";
import {
    DEFAULT_DATE,
    TODAY,
    TODAY_MINUS_14,
    TODAY_MINUS_30,
    DEFAULT_FILTER_ALL_PAGE_NUMBER,
    DEFAULT_FILTER_ALL_PAGE_SIZE
} from "../../Accounts/Account/Tables/DateFilters";
import formatDate from "../../Utils/Date/formatDate";
import InvoiceReview from "../InvoiceReview/InvoiceReview";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {fireGTMDownloadFileEvent} from "../../Utils/GTM/GTMHelper";


// import {Link} from "react-router-dom";


const getInvoices = async ({
    startDate,
    endDate,
    status,
    subAccountTypeCode,
    pageNumber,
    pageSize
}) => {
    let queryJson = {};
    if (pageNumber && pageSize) {
        queryJson = {
            pageNumber,
            pageSize,
            status,
            subAccountTypeCode,
        };
    } else {
        queryJson = {
            startDate,
            endDate,
            status,
            subAccountTypeCode,
        };
    }
    try {
        const invoices = await Get({
            endpoint: "/accounts/invoiceHeaders",
            queryJson,
        });
        if (!invoices) return false;
        return invoices;
    } catch (e) {
        console.error(e.message);
        return false;
    }
};

const filterMinStartDate = (invoices) => {
    return new Date(Math.min(...invoices.map(e => new Date(e.date))));
}

const getInvoiceLines = async ({ startDate, endDate, pageNumber, pageSize }) => {
    let queryJson = {};
    if (pageNumber && pageSize) {
        queryJson = {
            startDate,
            endDate,
            pageNumber,
            pageSize,
        };
    } else {
        queryJson = {
            startDate,
            endDate,
        };
    }

    try {
        const transactions = await Get({
            endpoint: "/accounts/invoiceLines",
            queryJson,
        });
        if (!transactions) return false;
        return transactions;
    } catch (e) {
        console.error(e.message);
        return false;
    }
};

const mapInvoice = (invoice) => {
    const name = invoice.subAccountName || invoice.accountName;
    const number = invoice.subAccountCode || invoice.accountCode;
    const address = invoice.subAccountAddress
        ? `${invoice.subAccountAddress.addressLine1}${
              invoice.subAccountAddress.addressLine2
                  ? `, ${invoice.subAccountAddress.addressLine2}`
                  : ""
          }, ${invoice.subAccountAddress.city}`
        : "User address";
    return {
        ...invoice,
        checkBox: invoice.invoiceNumber,
        invoiceAccount: {
            name,
            number,
            address,
        },
    };
};

const Invoices = () => {
    const fetchIdRef = React.useRef(0);
    const { user } = useContext(userContext);
    const { t, i18n } = useTranslation();
    const [invoices, setInvoices] = useState([]);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [pageNumber, setPageNumber] = useState(DEFAULT_FILTER_ALL_PAGE_NUMBER);
    const [pageSize, setPageSize] = useState(DEFAULT_FILTER_ALL_PAGE_SIZE);
    const [isSpecific, setIsSpecific] = useState(false);
    const [dateFilterType, setDateFilterType] = useState('last_all');
    const [accountTypes, setAccountTypes] = useState([]);
    const [accountType, setAccountType] = useState();
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [checked, setChecked] = useState([]);
    const [lastQuery, setLastQuery] = useState({});
	const [searchData, setSearchData] = useState([]);


    /* START DATEFILTERS FUNCTIONS */
    const dateFilterSetStartDate = (date) => {
        if (date != null) {
            const dateFormatted = `${date.toISOString().split("T")[0]}T00:00:00.000Z`;
            setStartDate(dateFormatted)
        }
    }
    const dateFilterSetEndDate = (date) => {
        if(date != null){
           const dateFormatted = `${date.toISOString().split("T")[0]}T23:59:59.999Z`;
           setEndDate(dateFormatted)
        }
    }
    const setLastAll = (refresh = true, component = null) => {
        setStartDate(null);
        setEndDate(null);
        setPageNumber(DEFAULT_FILTER_ALL_PAGE_NUMBER);
        setPageSize(DEFAULT_FILTER_ALL_PAGE_SIZE);
        setIsSpecific(false);
    }
    const setLast14Days = (refresh = true, component = null) => {
        setStartDate(formatDate(new Date(TODAY_MINUS_14)));
        setEndDate(formatDate(TODAY,'23:59:59.999Z'));
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(false);
    }
    const setLast30Days = () => {
        setStartDate(formatDate(new Date(TODAY_MINUS_30)));
        setEndDate(formatDate(TODAY,'23:59:59.999Z'));
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(false);
    }
    const setSpecificDate = () => {
        setStartDate(null);
        setEndDate(null);
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(true);
    }
    const dateFilterSetDateFilterType = (type) => {
        setDateFilterType(type)
        switch (type) {
            case 'last_all': setLastAll(); break;
            case 'last_14': setLast14Days(); break;
            case 'last_30': setLast30Days(); break;
            case 'specific_date': setSpecificDate(); break;
            default: setLastAll();
        }
    }
    /* END DATEFILTERS FUNCTIONS */

    useEffect(async () => {
        if(isSpecific && (startDate === null || endDate === null)){
            return null;
        }
        const currQuery = {
            startDate:startDate,
            endDate:endDate,
            subAccountTypeCode: accountType,
            pageNumber: pageNumber,
            pageSize: pageSize
        };

        if(JSON.stringify(currQuery) === JSON.stringify(lastQuery)){
            return null
        }else{
            setLastQuery(currQuery);
            setLoading(true);
        }

        user &&
           await getInvoices(currQuery).then((invoices) => {
                const mappedInvoices = invoices.data.map(mapInvoice);
                setInvoices(mappedInvoices);
                setPages(invoices.totalPages);
                setLoading(false);
                accountTypes.length === 0 &&
                    setAccountTypes(
                        Array.from(
                            new Set(
                                invoices.data.map((i) => i.accountType.code)
                            )
                        ).sort()
                    );
            });

    }, [user, accountType,dateFilterType,startDate,endDate,pageNumber,pageSize,setLastQuery,lastQuery]);

    useEffect(
        () =>
			 isSelectAll
				? setChecked(searchData.map((invoice) => invoice.invoiceNumber))
				: setChecked([])
		, [isSelectAll]
    );

    const handleCheck = (isChecked, value) => {
        if (isChecked && !checked.includes(value)) {
            setChecked((checked) => [
                ...checked.filter((thisChecked) => thisChecked !== value),
                value,
            ]);
        } else {
            setChecked((checked) =>
                checked.filter((thisChecked) => thisChecked !== value)
            );
        }
    };

    let sorter = null;
    const storeSortedItems = (items) => { return setInvoices(items); }
    const objectAccountSort = useMemo(
        () => (rowA, rowB, columnId) => {
            const a = parseInt(rowA.values[columnId].number);
            const b = parseInt(rowB.values[columnId].number);
            return a > b ? 1 : -1;
        },
        []
    );

    const columns = useMemo(
        () => [
            {
                Header: (
                    <div className="form-check flex flex-row justify-content-end align-items-end">
                        <input
                            type="checkbox"
                            id="select_all"
                            className="form-check-input"
                            value={isSelectAll}
                            onChange={(v) => setIsSelectAll(v.target.checked)}
                        ></input>
                    </div>
                ),
                accessor: "checkBox",
                Cell: (props) => {
                    return (
                        <div className="form-check flex flex-row justify-content-end">
                            <input
                                type="checkbox"
                                value={props.value}
                                className="form-check-input"
                                onChange={(v) => {
                                    handleCheck(
                                        v.target.checked,
                                        v.target.value
                                    );
                                }}
                                checked={!!checked.includes(props.value)}
                            ></input>
                        </div>
                    );
                },
                disableSortBy: true,
            },
            {
                Header: t("docs.invoices.table.headers.account"),
                accessor: "invoiceAccount",
                sortType: objectAccountSort,
                Cell: (props) => {
                    const { name, number, address } = props.value;
                    return (
                        <div className="flex flex-column justify-left">
                            <strong>
                                {name} - {number}
                            </strong>
                            <span>{address}</span>
							<a
								onClick={() => onClickInvoiceReviewHandler(props.row.original)}
								className="link link--red link--underline mt-1">
								<u>{t('docs.invoices.table.invoice_review_link')}</u>
							</a>
                        </div>
                    );
                },
            },
            {
                Header: t("docs.invoices.table.headers.invoice_no"),
                accessor: "invoiceNumber",
                sort: "desc",
                Cell: (props) => {
                    return <span>{props.value}</span>;
                },
                sortMethod: (a, b) => {
                    var a1 = new Date(a).getTime();
                    var b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
            },
            {
                Header: t("docs.invoices.table.headers.amount"),
                id: "netAmount",
                accessor: "netAmount.value",
                Cell: (props) => {
                    return (
                        <span className="amount-price">
                            <Currency value={props.value} />
                        </span>);
                },
            },
            {
                Header: t("docs.invoices.table.headers.due_date"),
                accessor: "dueDate",
                sort: "desc",
                Cell: (props) => {
                    return <DateFormated date={props.value} withTime={false} />;
                },
                sortMethod: (a, b) => {
                    var a1 = new Date(a).getTime();
                    var b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
            },
            {
                Header: t("docs.invoices.table.headers.transaction_date"),
                accessor: "transactionDateTime",
                sort: "desc",
                Cell: (props) => {
                    return <DateFormated date={props.value} withTime={false} />;
                },
                sortMethod: (a, b) => {
                    var a1 = new Date(a).getTime();
                    var b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
            },
            {
                Header: t("docs.invoices.table.headers.balance"),
                id: "balanceAmount",
                accessor: "balanceAmount.value",
                Cell: (props) => {
                    return (
                        <span className="amount-price">
                            <Currency value={props.value} />
                        </span>
                    );
                },
            },
            {
                Header: t("docs.invoices.table.headers.status"),
                id: 'transactionStatus',
                accessor:
                    i18n.language === "fr"
                        ? "transactionStatus.descriptionFr"
                        : "transactionStatus.descriptionEn",
                Cell: (props) => {
                    return props.value ? <span>{props.value}</span> : "";
                },
            },
            {
                Header: t("docs.invoices.table.headers.po"),
                accessor: "purchaseOrderNumber",
                Cell: (props) => {
                    return <span>{props.value}</span>;
                },
            },
            {
                Header: () => null,
                id: "file", // 'id' is required
                accessor: "documentPath",
                Cell: (props) => {
                    if (props.value) {
                        return (
                            <a
                                href={`/rest/v1/communications/download?filename=${encodeURI(
                                    props.value
                                )}`}
								onClick={()=>fireGTMDownloadFileEvent('facture','PDF')}
                                className="link link--red link--upper link--accent link--fs-14"
                                target="_blank"
                            >
                                <strong>
                                    <i className="far fa-file-download fa-2x"></i>
                                </strong>
                            </a>
                        );
                    } else {
                        return "";
                    }
                },
            },
        ],
        [t, i18n, checked]
    );

    const doXLSExport = useCallback((fileType) => {
        const checkedInvoices = invoices
            .filter((invoice) =>
                checked.includes(invoice.invoiceNumber.toString())
            )
            .map((invoice) => ({
                [t("export.invoices.subAccountCode")]: invoice.subAccountCode,
                [t("export.invoices.transactionDateTime")]:
                    new Date(invoice.transactionDateTime.split('T')[0]),
                [t("export.invoices.invoiceNumber")]: invoice.invoiceNumber,
                [t("export.invoices.dueDate")]: new Date(invoice.dueDate.split('T')[0]),
                [t("export.invoices.netAmount")]: invoice.netAmount.value,
                [t("export.invoices.balanceAmount")]:
                    invoice.balanceAmount.value,
                [t("export.invoices.poNumber")]: invoice.purchaseOrderNumber,
            }));
        Export(checkedInvoices, "harnois_export", [
            t("export.invoices.netAmount"),
            t("export.invoices.balanceAmount"),
        ], fileType);
		fireGTMDownloadFileEvent('facture',fileType + ' - Sommaire');
	}, [checked, invoices]);

    const doXLSDetailExport = useCallback(async (fileType) => {
        const transactions =
            user &&
            (await getInvoiceLines({
                startDate: (pageNumber && pageSize) ? formatDate(filterMinStartDate(invoices)) : `${startDate.split("T")[0]}T00:00:00.000Z`,
                endDate: (pageNumber && pageSize) ? formatDate(TODAY,'23:59:59.999Z') : `${endDate.split("T")[0]}T23:59:59.999Z`,
                pageNumber,
                pageSize

            }));

        const mappedTransactions =
            transactions &&
            transactions.data &&
            transactions.data
                .filter((transaction) =>
                    checked.includes(transaction.invoiceNumber.toString())
                )
                .map((transaction) => {
                    return {
                        [t("export.invoiceLines.subAccountCode")]:
                            transaction.subAccountCode,
                        [t("export.invoiceLines.invoiceNumber")]:
                            transaction.invoiceNumber,
                        [t("export.invoiceLines.transactionDateTime")]:
                            new Date(transaction.transactionDateTime+'Z'),
                        /*[t(
                            "export.invoiceLines.truckName"
                        )]: transaction.truckName,*/
                        [t("export.invoiceLines.productCode")]:
                            transaction.productCode,
                        [t("export.invoiceLines.productName")]:
                            transaction.productName.values[
                                transaction.productName.keys.indexOf(
                                    i18n.language
                                )
                            ],
                        [t("export.invoiceLines.quantity")]: Number(
                            Number(transaction.quantity.value).toFixed(3)
                        ),
                        [t("export.invoiceLines.priceAfterExciseTaxes")]:
                            transaction.priceAfterExciseTaxes.value,
                        [t("export.invoiceLines.amountAfterExciseTaxes")]:
                            transaction.amountAfterExciseTaxes.value,
                        [t("export.invoiceLines.gstAmount")]:
                            transaction.gstAmount.value,
                        [t("export.invoiceLines.pstAmount")]:
                            transaction.pstAmount.value,
                        [t("export.invoiceLines.netAmount")]:
                            transaction.netAmount.value,
                        [t("export.invoiceLines.date")]:
                            transaction.transactionDateTime
                                .split("T")[0],
                        [t("export.invoiceLines.time")]:
                            transaction.transactionDateTime.split("T")[1],
                        [t("export.invoiceLines.carrierDeliveryNumber")]:
                            transaction.carrierDeliveryNumber,
                        [t(
                            "export.invoiceLines.truckName"
                        )]: transaction.truckName,
                        [t(
                            "export.invoiceLines.poNumber"
                        )]: transaction.purchaseOrderNumber,
                    };
                });
        Export(mappedTransactions, "harnois_export", [
            t("export.invoiceLines.netAmount"),
        ], fileType);
		fireGTMDownloadFileEvent('facture',fileType+' - Detaille');
		setLoading(false);
    }, [user, startDate, endDate, pageNumber, pageSize, checked, i18n]);

    const doPDFExport = useCallback(async () => {

        const checkedInvoicesPDF = invoices
            .filter((invoice) =>
                checked.includes(invoice.invoiceNumber.toString())
            );
        let filesPathString = "";
        checkedInvoicesPDF.map((item, index) => {
            if(item.documentPath !== ""){
                filesPathString += (item.documentPath);
                if((checkedInvoicesPDF.length > 1 ) && index !== checkedInvoicesPDF.length-1){ filesPathString += ","; }
            }
        });
       await(axios.get(`mergepdf?filenames=`+filesPathString)
            .then(response => {
				console.log(response.data)
                download('Invoices.pdf', response.data);

            }).catch(error => { console.log(error.message); }));

    }, [checked]);

    function download(filename, text) {
        var element = document.createElement('a');
        element.setAttribute('href', 'data:application/pdf;base64,' + encodeURIComponent(text));
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    let sorterValues = [
        {
            label: t("docs.invoices.table.headers.transaction_date"),
            id: 'transactionDateTime',
            value: 'transactionDateTime',
            type: 'date'
        },{
            label: t("docs.invoices.table.headers.due_date"),
            id: 'dueDate',
            value: '-dueDate',
            type: 'date'
        },{
            label: t("docs.invoices.table.headers.account"),
            id: 'invoiceAccount',
            value: 'invoiceAccount.number',
            type: 'int'
        },{
            label: t("docs.invoices.table.headers.invoice_no"),
            id: 'invoiceNumber',
            value: 'invoiceNumber',
            type: 'int'
        },{
            label: t("docs.invoices.table.headers.amount"),
            id: 'netAmount',
            value: 'netAmount.value',
            type: 'float'
        },{
            label: t("docs.invoices.table.headers.balance"),
            id: 'balanceAmount',
            value: 'balanceAmount.value',
            type: 'float'
        },{
            label: t("docs.invoices.table.headers.status"),
            id: 'transactionStatus',
            value: i18n.language === 'fr' ? 'transactionStatus.descriptionFr': 'transactionStatus.descriptionEn',
            type: 'string'
        },{
            label: t("docs.invoices.table.headers.po"),
            id: 'purchaseOrderNumber',
            value: 'purchaseOrderNumber',
            type: 'string'
        },
    ];


    // Modal Invoice Review
    const [invoiceReview, setInvoiceReview] = useState({ open: false, data: {}});

    const onClickInvoiceReviewHandler = (invoiceData) => {
        setInvoiceReview({ open: true, data: invoiceData});
    }

    const hideInvoiceReviewHandler = () => {
        setInvoiceReview({ open: false, data: []});
    }


    return (
        <div className="documents">
            <div className="container container--larger flex nopadding">
                <h2 className="headline headline--section">
                    {t("docs.invoices.title")}
                </h2>
                <div className="row">
                    <div className="col-12"></div>
                </div>
            </div>
            <InvoiceTable
                columns={columns}
                data={invoices}
                pageCount={pages}
                loading={loading}
                checked={checked}
                doXLSExport={doXLSExport}
                doXLSDetailExport={doXLSDetailExport}
                doPDFExport={doPDFExport}
                startDate={startDate}
                setStartDate={dateFilterSetStartDate}
                endDate={endDate}
                setEndDate={dateFilterSetEndDate}
                accountTypes={accountTypes}
                accountType={accountType}
                setAccountType={setAccountType}
                getSortedItems={storeSortedItems}
                sorterValues={sorterValues}
                showExportPdf={true}
                showExportXml={true}
                showUnpaid={true}
                setDateFilterType={dateFilterSetDateFilterType}
                dateFilterType={dateFilterType}
                initialSortField='dueDate'
                labelFilterAll={t("docs.invoices.table.filter.all.invoices")}
				searchData={searchData}
				setSearchData={setSearchData}
            />
            <InvoiceReview showInvoiceReview={invoiceReview} hideInvoiceReview={hideInvoiceReviewHandler} />
        </div>
    );
};

export default Invoices;
